/**
 * Tutorial helper — call from page components after key actions to auto-complete missions.
 *
 * Usage:
 *   import { markMissionComplete } from '../lib/tutorialActions';
 *   await markMissionComplete('analyze_tender');
 *
 * Idempotent — backend handles duplicate calls.
 * Silent fail — never blocks user flow if tutorial state unavailable.
 */

import { api } from './api';
import { resetAllTours } from '../components/tutorial/Tour';

let lastSuppressTime = {};
const SUPPRESS_WINDOW_MS = 5000;  // Don't double-call same mission within 5s


export async function markMissionComplete(missionKey) {
  // Suppress duplicate calls within window
  const now = Date.now();
  if (lastSuppressTime[missionKey] && now - lastSuppressTime[missionKey] < SUPPRESS_WINDOW_MS) {
    return;
  }
  lastSuppressTime[missionKey] = now;

  try {
    await api.post(`/tutorial/missions/${missionKey}/complete`, { auto: true });
  } catch (err) {
    // Silent fail — tutorial may not be active, user may not be in onboarding
    console.debug('Mission auto-complete failed for', missionKey, err.response?.status);
  }
}


/**
 * Map common actions to mission keys.
 * Call from page navigation / button handlers.
 */
export const MISSIONS = {
  BROWSE_TENDERS: 'browse_tenders',
  ANALYZE_TENDER: 'analyze_tender',
  EXTRACT_BOQ: 'extract_boq',
  SELECT_SUPPLIERS: 'select_suppliers',
  SEND_RFQ: 'send_rfq',
  VIEW_QUOTES: 'view_quotes',
  CHECK_VERDICT: 'check_verdict',
  GENERATE_OFFER: 'generate_offer',
  GENERATE_JEDZ: 'generate_jedz',
  MARK_SUBMITTED: 'mark_submitted',
  RECORD_OUTCOME: 'record_outcome',
};


/**
 * Restart tutorial — clears welcome_seen + completed_missions, reopens Welcome Modal.
 * Call from Settings → "Pokaż samouczek od nowa".
 */
export async function restartTutorial() {
  try {
    await api.post('/tutorial/restart');
    // Reset suppression cache so re-doing actions retriggers missions
    lastSuppressTime = {};
    // Also wipe localStorage flags for in-context Joyride-style tours
    resetAllTours();
    return { ok: true };
  } catch (err) {
    return { ok: false, error: err.response?.data?.detail || err.message };
  }
}


/**
 * Get current tutorial state — returns { welcome_seen, completed_missions, ... }
 */
export async function getTutorialState() {
  try {
    const r = await api.get('/tutorial/state');
    return r.data;
  } catch (err) {
    return null;
  }
}


/**
 * Dismiss tutorial — user clicked "X" or "enough" — hides Mission Board permanently.
 * User can resume from Settings → Restart.
 */
export async function dismissTutorial() {
  try {
    await api.post('/tutorial/dismiss');
    return { ok: true };
  } catch (err) {
    return { ok: false, error: err.response?.data?.detail || err.message };
  }
}
